import React, { useEffect, useState } from 'react'
import Iframe from 'react-iframe'
import { Loader } from 'semantic-ui-react';
import { useHybridAuth } from '../auth/HybridAuthContext';
import {
    getDriverSyncToken,
    refreshDriverSyncToken,
    clearDriverSyncToken
} from '../utils/driverSync';

type Props = {
}

const DRIVER_SYNC_ORIGIN = 'https://driversyncforportal.fetchgoat.com';

const DriverSync = (props: Props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [dsToken, setDsToken] = useState<string | null>(getDriverSyncToken());
    const { authType } = useHybridAuth();

    /*
     * Initial DriverSync authentication.
     */
    useEffect(() => {
        const existing = getDriverSyncToken();

        if (existing) {
            setDsToken(existing);
            setIsLoading(false);
            return;
        }

        if (authType !== 'sso') {
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        setIsLoading(true);

        refreshDriverSyncToken()
            .then(token => {
                if (!cancelled && token) {
                    setDsToken(token);
                }
            })
            .catch(err => {
                console.error('DriverSync initial authentication failed:', err);

                if (!cancelled) {
                    clearDriverSyncToken();

                    // Do NOT log the Portal user out.
                    setDsToken(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authType]);


    /*
     * DriverSync can request reauthentication when its own
     * session/refresh token has expired.
     */
    useEffect(() => {
        const handleDriverSyncMessage = async (event: MessageEvent) => {

            // Only accept messages from our DriverSync site.
            if (event.origin !== DRIVER_SYNC_ORIGIN) {
                return;
            }

            if (event.data?.type !== 'driversync-auth-required') {
                return;
            }

            console.log('DriverSync requested reauthentication');

            if (authType !== 'sso') {
                console.warn(
                    'DriverSync requested reauthentication, but Portal is not using SSO'
                );
                return;
            }

            try {
                setIsLoading(true);

                // Remove the expired DS token first.
                clearDriverSyncToken();

                /*
                 * This should use the still-valid Portal SSO session
                 * to obtain a new driversync-handoff token and exchange
                 * it for a new DriverSync login.
                 */
                const token = await refreshDriverSyncToken();

                if (!token) {
                    throw new Error('No DriverSync token returned');
                }

                setDsToken(token);

                console.log('DriverSync reauthentication successful');
            }
            catch (err) {
                console.error(
                    'DriverSync reauthentication failed:',
                    err
                );

                clearDriverSyncToken();
                setDsToken(null);

                /*
                 * IMPORTANT:
                 * Do not destroy the Portal SSO session here.
                 * DriverSync failing should not log the Portal out.
                 */
            }
            finally {
                setIsLoading(false);
            }
        };

        window.addEventListener('message', handleDriverSyncMessage);

        return () => {
            window.removeEventListener(
                'message',
                handleDriverSyncMessage
            );
        };
    }, [authType]);


    return (
        <div
            style={{
                width: '80%',
                display: 'flex',
                margin: '1rem auto',
                height: '86vh'
            }}
        >
            {isLoading &&
                <Loader
                    active
                    inline='centered'
                    size='large'
                >
                    Loading...
                </Loader>
            }

            {dsToken &&
                <Iframe
                    key={dsToken}
                    url={`${DRIVER_SYNC_ORIGIN}/dashboard?token=${encodeURIComponent(dsToken)}`}
                    width="100%"
                    height="100%"
                    scrolling='yes'
                    onLoad={() => setIsLoading(false)}
                />
            }
        </div>
    )
}

export default DriverSync